import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  User as UserIcon,
  Send,
  Bot,
  LogOut,
  Pencil,
  Bell,
  ThumbsUp,
  ThumbsDown,
  Flag,
  Lock,
  X,
  Menu,
} from 'lucide-react';
import { Event, User, Notification, SignupFormField } from '../../types';
import { eventsApi, apiService, notificationsApi, feedbackApi, callbackRequestsApi } from '../../services/api';
import { chatSSEService } from '../../services/chatSSEService';
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll';
import { COUNTRY_CODES } from '../../config';

interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  metadata?: {
    isRAG?: boolean;
    chunksUsed?: number;
    processingTime?: number;
    tokensUsed?: number;
  };
}

// ---------------------------------------------------------------------------
// Small reusable components
// ---------------------------------------------------------------------------

const Spinner: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
  const sz = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';
  return (
    <svg
      className={`animate-spin ${sz} text-blue-600`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const EventLandingPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const isWhatsAppFlow = new URLSearchParams(window.location.search).get('source') === 'wa';

  // State Management
  const [event, setEvent] = useState<Event | null>(null);
  const [showWaThankYou, setShowWaThankYou] = useState(false);
  const [signupFormFields, setSignupFormFields] = useState<SignupFormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [chatVisible, setChatVisible] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [apiKeysNotConfigured, setApiKeysNotConfigured] = useState(false);

  // Refs for synchronous duplicate prevention (state is async and unreliable for guards)
  const isProcessingRef = useRef(false);
  const activeStreamRef = useRef<string | null>(null);
  const chatInitializedRef = useRef(false);
  const { containerRef: messagesContainerRef, handleScroll: handleMessagesScroll, enableAutoScroll } =
    useChatAutoScroll(messages);

  // RAG Mode (fixed ON; UI toggle hidden). ragTopK comes from event settings on the backend.
  const useRAG = true;
  // Removed unused recentChats and activeSessionId state
  const [isLogin, setIsLogin] = useState(false);
  // Removed unused sidebarVisible state
  const [isMobile, setIsMobile] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [reportModal, setReportModal] = useState<{ open: boolean; messageId?: string } | null>(null);
  const [submittingFeedbackFor, setSubmittingFeedbackFor] = useState<string | null>(null);
  const [callbackModalVisible, setCallbackModalVisible] = useState(false);

  // Password reset state
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);

  // Email verification (OTP) state
  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpResendLoading, setOtpResendLoading] = useState(false);
  const [otpVerificationId, setOtpVerificationId] = useState<string | null>(null);
  const [signupLoading, setSignupLoading] = useState(false);

  // Plain-state forms (replacing antd Form.useForm)
  const [signupValues, setSignupValues] = useState<Record<string, any>>({});
  const [signupErrors, setSignupErrors] = useState<Record<string, string>>({});
  const [phoneCountryCode, setPhoneCountryCode] = useState('+91');

  const [loginValues, setLoginValues] = useState<Record<string, any>>({});
  const [loginErrors, setLoginErrors] = useState<Record<string, string>>({});

  const [profileValues, setProfileValues] = useState<Record<string, any>>({});
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});

  const [reportValues, setReportValues] = useState<Record<string, any>>({});
  const [reportErrors, setReportErrors] = useState<Record<string, string>>({});

  const [callbackValues, setCallbackValues] = useState<Record<string, any>>({});
  const [callbackErrors, setCallbackErrors] = useState<Record<string, string>>({});

  const [forgotPasswordValues, setForgotPasswordValues] = useState<Record<string, any>>({});
  const [forgotPasswordErrors, setForgotPasswordErrors] = useState<Record<string, string>>({});

  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpChannel, setOtpChannel] = useState<'email' | 'whatsapp'>('email');

  // Profile dropdown open state
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // Mobile hamburger menu
  const [menuOpen, setMenuOpen] = useState(false);

  // Notifications state
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [_unreadCount, setUnreadCount] = useState<number>(0);
  const notifPollingRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFetchedAtRef = React.useRef<string | null>(null);

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  // Handle mobile detection and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Notifications: start polling when event loaded
  useEffect(() => {
    if (!event) return;
    fetchNotifications();

    if (notifPollingRef.current) clearInterval(notifPollingRef.current);
    notifPollingRef.current = setInterval(fetchNotifications, 30000);
    return () => {
      if (notifPollingRef.current) clearInterval(notifPollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?._id]);

  const fetchNotifications = async () => {
    try {
      if (!event) return;
      const since = lastFetchedAtRef.current || undefined;
      const resp = await notificationsApi.listForEvent(event._id, { since });
      const list = resp.data.data?.notifications || [];
      if (list.length) {
        setNotifications((prev) => {
          const existingIds = new Set(prev.map((n) => n._id));
          const newOnes = list.filter((n) => !existingIds.has(n._id));
          const merged = [...newOnes, ...prev];
          if (newOnes.length) setUnreadCount((count) => count + newOnes.length);
          return merged.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        });
      }
      lastFetchedAtRef.current = new Date().toISOString();
    } catch (e) {
      // silent
    }
  };

  const _openNotifications = async () => {
    setNotifOpen(true);
    try {
      const currentUserId = user?.userId;
      await Promise.all(
        notifications.map((n) =>
          notificationsApi
            .markSeen(n._id, currentUserId ? { userId: currentUserId } : undefined)
            .catch(() => null)
        )
      );
      setUnreadCount(0);
    } catch {}
  };

  // Fetch Event Data
  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        toast.error('Knowledge Assistant ID not provided');
        navigate('/');
        return;
      }

      try {
        setLoading(true);
        const response = await eventsApi.getById(eventId);

        if (response.data.success && response.data.data) {
          const eventData = response.data.data;
          if (eventData.status === 'inactive' || (!eventData.status && !eventData.isActive)) {
            toast.error('Knowledge Assistant is currently not active. Please contact your Admin.');
            return;
          }
          setEvent(eventData);
          try {
            const signupRes = await eventsApi.getSignupForm(eventId);
            if (signupRes.data.success && signupRes.data.data?.signupFormFields?.length) {
              const sorted = [...signupRes.data.data.signupFormFields].sort(
                (a, b) => a.order - b.order
              );
              setSignupFormFields(sorted);
            } else {
              setSignupFormFields([]);
            }
          } catch {
            setSignupFormFields([]);
          }
        } else {
          toast.error('Knowledge Assistant not found');
          navigate('/');
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        toast.error('Failed to load event information');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, navigate]);

  // ---------------------------------------------------------------------------
  // Validation helpers
  // ---------------------------------------------------------------------------

  const validateEmail = (v: string) =>
    !v ? 'Please enter your email' : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Please enter a valid email' : '';

  const validateRequired = (v: any, label: string) =>
    !v || String(v).trim() === '' ? `Please enter ${label}` : '';

  // ---------------------------------------------------------------------------
  // Signup
  // ---------------------------------------------------------------------------

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const values = signupValues;
    const errors: Record<string, string> = {};

    const nameErr = validateRequired(values.name, 'your name');
    if (nameErr) errors.name = nameErr;
    const emailRequired = !!(event as any)?.requireEmail;
    if (emailRequired) {
      const emailErr = validateEmail(values.email || '');
      if (emailErr) errors.email = emailErr;
    } else if (values.email) {
      const fmtErr = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email) ? 'Please enter a valid email' : '';
      if (fmtErr) errors.email = fmtErr;
    }
    const phoneRequired = (event as any)?.otpChannel === 'whatsapp' || !!(event as any)?.requirePhone;
    if (phoneRequired) {
      const phoneErr = validateRequired(values.phone, 'your phone number');
      if (phoneErr) errors.phone = phoneErr;
    }
    const passErr = !values.password
      ? 'Please enter a password'
      : values.password.length < 6
      ? 'Password must be at least 6 characters'
      : '';
    if (passErr) errors.password = passErr;

    signupFormFields.forEach((f) => {
      if (f.isRequired) {
        const v = values[f.fieldCode];
        if (!v || String(v).trim() === '')
          errors[f.fieldCode] = `Please enter ${f.fieldName}`;
      }
    });

    if (Object.keys(errors).length) {
      setSignupErrors(errors);
      return;
    }
    setSignupErrors({});

    const customFields: Record<string, any> = {};
    signupFormFields.forEach((f) => {
      const v = values[f.fieldCode];
      if (v !== undefined && v !== null && v !== '') customFields[f.fieldCode] = v;
    });

    setSignupLoading(true);
    try {
      const response = await apiService.post('/users', {
        name: values.name,
        email: values.email,
        password: values.password,
        phone: values.phone ? `${phoneCountryCode.replace('+', '')}${values.phone.trim()}` : undefined,
        occupation: values.occupation || '',
        businessNature: values.businessNature || '',
        businessDetails: values.businessDetails || '',
        eventId: eventId || undefined,
        customFields: Object.keys(customFields).length ? customFields : undefined,
      });

      if (response.success && response.data) {
        const payload: any = response.data;
        const createdUser = payload.user || payload;
        setSignupValues({});

        if (payload.verificationRequired) {
          setOtpVerificationId(String(payload.verificationId));
          setOtpChannel(payload.channel === 'whatsapp' ? 'whatsapp' : 'email');
          setOtpModalVisible(true);
          toast.info(payload.channel === 'whatsapp'
            ? 'We sent a verification code to your WhatsApp. Enter it to continue.'
            : 'We sent a verification code to your email. Enter it to continue.');
          return;
        }

        if (isWhatsAppFlow) {
          setShowWaThankYou(true);
        } else {
          setUser(createdUser);
          sessionStorage.setItem(`event_user_${eventId}`, JSON.stringify(createdUser));
          toast.success('Welcome! Starting your chat session...');
          await loadUserSessions(createdUser.userId);
          setChatVisible(true);
        }
      } else {
        toast.error((response as any).message || 'Failed to create account. Please try again.');
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
      if (backendMessage) {
        toast.error(backendMessage);
      } else {
        toast.error('Failed to create account. Please try again.');
      }
    } finally {
      setSignupLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // OTP
  // ---------------------------------------------------------------------------

  const handleVerifyOtp = async () => {
    if (!otpVerificationId) return;
    if (!otpValue || !/^[0-9]{6}$/.test(otpValue)) {
      setOtpError('Enter a 6-digit code');
      return;
    }
    setOtpError('');
    setOtpLoading(true);
    try {
      const resp = await apiService.post('/otp/email/verify', {
        verificationId: otpVerificationId,
        otp: otpValue,
      });
      if (resp.success && resp.data?.user) {
        setOtpModalVisible(false);
        setOtpValue('');
        if (isWhatsAppFlow) {
          setShowWaThankYou(true);
        } else {
          setUser(resp.data.user);
          sessionStorage.setItem(`event_user_${eventId}`, JSON.stringify(resp.data.user));
          toast.success('Email verified. Starting chat...');
          await loadUserSessions(resp.data.user.userId);
          setChatVisible(true);
        }
      } else {
        toast.error('Verification failed. Please try again.');
      }
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message;
      if (backendMessage) toast.error(backendMessage);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpVerificationId) return;
    setOtpResendLoading(true);
    try {
      const endpoint = otpChannel === 'whatsapp' ? '/otp/whatsapp/resend' : '/otp/email/resend';
      const resp = await apiService.post(endpoint, { verificationId: otpVerificationId });
      if (resp.success) {
        toast.success('Verification code resent.');
      } else {
        toast.error('Could not resend code.');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not resend code.');
    } finally {
      setOtpResendLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Login
  // ---------------------------------------------------------------------------

  const loginReqEmail = !!(event as any)?.requireEmail;
  const loginReqPhone = !!(event as any)?.requirePhone;
  const loginFieldLabel = loginReqEmail && !loginReqPhone ? 'Email Address'
    : !loginReqEmail && loginReqPhone ? 'Phone Number'
    : 'Email or Phone Number';
  const loginFieldPlaceholder = loginReqEmail && !loginReqPhone ? 'Enter your email'
    : !loginReqEmail && loginReqPhone ? 'Enter your phone number'
    : 'Enter your email or phone number';
  const loginFieldType = loginReqEmail && !loginReqPhone ? 'email' : 'text';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const values = loginValues;
    const errors: Record<string, string> = {};
    const identifier = (values.identifier || '').trim();
    if (!identifier) {
      errors.identifier = loginReqEmail && !loginReqPhone
        ? 'Please enter your email'
        : !loginReqEmail && loginReqPhone
        ? 'Please enter your phone number'
        : 'Please enter your email or phone number';
    } else if (loginReqEmail && !loginReqPhone) {
      const emailErr = validateEmail(identifier);
      if (emailErr) errors.identifier = emailErr;
    } else if (identifier.includes('@')) {
      const emailErr = validateEmail(identifier);
      if (emailErr) errors.identifier = emailErr;
    }
    if (!values.password) errors.password = 'Please enter your password';

    if (Object.keys(errors).length) {
      setLoginErrors(errors);
      return;
    }
    setLoginErrors({});

    try {
      const response = await apiService.post('/users/login', {
        identifier,
        password: values.password,
        eventId: eventId || undefined,
      });

      if (response.success && response.data) {
        const payload = response.data;

        if (payload.verificationRequired) {
          setOtpVerificationId(String(payload.verificationId));
          setOtpChannel(payload.channel === 'whatsapp' ? 'whatsapp' : 'email');
          setOtpModalVisible(true);
          toast.info(payload.channel === 'whatsapp'
            ? 'Please verify via WhatsApp. We sent a code to your WhatsApp number.'
            : 'Please verify your email to continue. We sent a code to your email.');
          return;
        }

        setUser(payload);
        sessionStorage.setItem(`event_user_${eventId}`, JSON.stringify(payload));
        toast.success(`Welcome back, ${payload.name}! Opening chat...`);
        setLoginValues({});
        await loadUserSessions(payload.userId);
        setChatVisible(true);
      } else {
        toast.error('Invalid credentials. Please try again.');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const backendMessage = error?.response?.data?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      } else if (error?.response?.status === 401) {
        toast.error('Invalid email or password. Please try again.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    }
  };

  // Load user's recent chat sessions
  const loadUserSessions = async (_userId: string) => {
    try {
      const response = await apiService.get(`/users/${_userId}/sessions?limit=10`);
      if (response.success && response.data) {
        // No-op: recent chats sidebar is not displayed currently
      }
    } catch (error) {
      console.error('Error loading user sessions:', error);
    }
  };

  // Handle profile update
  const handleUpdateProfile = () => {
    if (user) {
      setProfileModalVisible(true);
      setProfileValues({
        name: user.name,
        email: user.email,
        occupation: user.occupation,
        businessNature: user.businessNature,
        businessDetails: user.businessDetails,
      });
    }
  };

  // Handle profile save
  const handleProfileSave = async () => {
    const values = profileValues;
    const errors: Record<string, string> = {};
    const nameErr = validateRequired(values.name, 'your name');
    if (nameErr) errors.name = nameErr;
    const emailErr = validateEmail(values.email || '');
    if (emailErr) errors.email = emailErr;

    if (Object.keys(errors).length) {
      setProfileErrors(errors);
      return;
    }
    setProfileErrors({});

    if (!user) return;

    try {
      const response = await apiService.put(`/users/${user.userId}`, {
        name: values.name,
        email: values.email,
        occupation: values.occupation || '',
        businessNature: values.businessNature || '',
        businessDetails: values.businessDetails || '',
      });

      if (response.success && response.data) {
        setUser(response.data);
        setProfileModalVisible(false);
        toast.success('Profile updated successfully!');
        setProfileValues({});
      } else {
        toast.error('Failed to update profile. Please try again.');
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile. Please try again.');
    }
  };

  // Handle sign out
  const handleSignOut = () => {
    sessionStorage.removeItem(`event_user_${eventId}`);
    setUser(null);
    setMessages([]);
    setSessionId(null);
    setChatVisible(false);
    toast.success('You have been signed out successfully.');
  };

  const clearChat = () => {
    isProcessingRef.current = false;
    chatInitializedRef.current = false;
    activeStreamRef.current = null;
    setChatLoading(false);
    setInputMessage('');
    setMessages([]);
    setSessionId(null);
    setTimeout(() => {
      startChat();
    }, 0);
  };

  const submitCallbackRequest = async () => {
    const values = callbackValues;
    const errors: Record<string, string> = {};
    if (!values.issue || !String(values.issue).trim()) errors.issue = 'Please describe your issue';
    if (!values.expectedResolution || !String(values.expectedResolution).trim())
      errors.expectedResolution = 'Please describe the expected resolution';

    if (Object.keys(errors).length) {
      setCallbackErrors(errors);
      return;
    }
    setCallbackErrors({});

    if (!event || !user) return;
    const phone = (user as any).phone;
    if (!phone || !String(phone).trim()) {
      toast.error('Phone number is missing. Please sign up again with a phone number.');
      return;
    }
    try {
      await callbackRequestsApi.create({
        eventId: event._id,
        userId: user.userId,
        sessionId: sessionId || undefined,
        phone: String(phone).trim(),
        issue: values.issue,
        expectedResolution: values.expectedResolution,
      });
      toast.success('Callback request submitted. We will contact you shortly.');
      setCallbackModalVisible(false);
      setCallbackValues({});
    } catch (e: any) {
      if (e?.response?.data?.error) {
        toast.error(e.response.data.error);
      }
    }
  };

  // Handle forgot password request
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const values = forgotPasswordValues;
    const errors: Record<string, string> = {};
    const emailErr = validateEmail(values.email || '');
    if (emailErr) errors.email = emailErr;

    if (Object.keys(errors).length) {
      setForgotPasswordErrors(errors);
      return;
    }
    setForgotPasswordErrors({});

    setPasswordResetLoading(true);
    try {
      const response = await apiService.post('/users/forgot-password', {
        email: values.email,
        eventId,
      });

      if (response.success) {
        toast.success(
          'A new password has been sent to your email! Please check your inbox and use it to login.'
        );
        setForgotPasswordValues({});
        setForgotPasswordVisible(false);
      } else {
        toast.error('Failed to send new password. Please try again.');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      toast.error('An error occurred. Please try again later.');
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleQuickFeedback = async (messageId: string, rating: number, summary: string) => {
    if (!event || submittingFeedbackFor) return;
    try {
      setSubmittingFeedbackFor(messageId);
      await feedbackApi.create({
        sessionId: sessionId || undefined,
        messageId,
        eventId: event._id,
        rating,
        summary,
        category: 'other',
      });
      toast.success('Thanks for the feedback!');
    } catch (e) {
      toast.error('Could not submit feedback. Please try again.');
    } finally {
      setSubmittingFeedbackFor(null);
    }
  };

  const submitReport = async () => {
    const values = reportValues;
    const errors: Record<string, string> = {};
    if (values.rating === undefined || values.rating === null || values.rating === '')
      errors.rating = 'Required';
    if (!values.category) errors.category = 'Required';
    if (!values.summary || !String(values.summary).trim()) errors.summary = 'Required';

    if (Object.keys(errors).length) {
      setReportErrors(errors);
      return;
    }
    setReportErrors({});

    try {
      if (!event) return;
      await feedbackApi.create({
        sessionId: sessionId || undefined,
        messageId: reportModal?.messageId,
        eventId: event._id,
        rating: Number(values.rating),
        category: values.category,
        summary: values.summary,
        details: values.details,
      });
      setReportModal(null);
      setReportValues({});
      toast.success('Thanks for the feedback!');
    } catch (e: any) {
      const backendMessage = e?.response?.data?.error || e?.response?.data?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      } else {
        toast.error('Could not submit feedback. Please try again.');
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Chat
  // ---------------------------------------------------------------------------

  const startChat = useCallback(async () => {
    console.log('startChat called with user:', user?.name, 'event:', event?.name, 'initialized:', chatInitializedRef.current);

    if (!user || !event) {
      console.log('startChat: Missing user or event, returning early');
      return;
    }

    if (isProcessingRef.current || chatInitializedRef.current) {
      console.log('startChat: Already processing or initialized, skipping duplicate call');
      return;
    }

    enableAutoScroll();
    isProcessingRef.current = true;
    chatInitializedRef.current = true;

    setSessionId(null);
    if (!chatVisible) {
      setChatVisible(true);
    }

    setChatLoading(true);
    console.log('startChat: Starting streaming API call...');

    try {
      const chatRequest = {
        userId: user.userId,
        eventId: event._id,
        message: 'start',
        sessionId: undefined,
        useRAG: useRAG,
      };

      console.log('Starting chat with streaming request:', chatRequest);

      const streamSessionId = `start_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      activeStreamRef.current = streamSessionId;

      const placeholderId = `placeholder_${streamSessionId}`;
      let realConversationId: string | null = null;

      console.log('[CHAT] Creating placeholder message:', placeholderId);
      setMessages([
        {
          id: placeholderId,
          type: 'bot',
          content: '',
          timestamp: new Date(),
          metadata: { isRAG: useRAG, chunksUsed: 0 },
        },
      ]);

      chatSSEService.connect(streamSessionId, {
        onConnect: () => {
          console.log('[CHAT] SSE connected for start message, streamId:', streamSessionId);
          if (activeStreamRef.current !== streamSessionId) return;

          apiService
            .post('/chat/stream', { ...chatRequest, streamSessionId })
            .catch((error) => {
              console.error('[CHAT] Failed to start processing:', error);
              isProcessingRef.current = false;
              setChatLoading(false);
              setMessages([
                {
                  id: Date.now().toString(),
                  type: 'bot',
                  content:
                    error?.response?.data?.message || 'Failed to start chat. Please try again.',
                  timestamp: new Date(),
                },
              ]);
              chatSSEService.disconnect(`chat_${streamSessionId}`);
            });
        },
        onStart: (convId: string) => {
          console.log('[CHAT] Response started:', convId, 'placeholderId:', placeholderId);
          if (activeStreamRef.current !== streamSessionId) return;

          realConversationId = convId;
          setMessages((prev) => {
            console.log('[CHAT] onStart - updating messages:', prev.map((m) => m.id));
            return prev.map((msg) => {
              if (msg.id === placeholderId) {
                console.log('[CHAT] onStart - updating placeholder to:', convId);
                return { ...msg, id: convId };
              }
              return msg;
            });
          });
        },
        onToken: (text: string, convId: string) => {
          if (activeStreamRef.current !== streamSessionId) return;

          console.log(
            '[CHAT] Token received:',
            text.substring(0, 20) + '...',
            'convId:',
            convId,
            'realConversationId:',
            realConversationId,
            'placeholderId:',
            placeholderId
          );

          setMessages((prev) => {
            console.log('[CHAT] Current messages:', prev.map((m) => ({ id: m.id, contentLen: m.content.length })));

            const idx = prev.findIndex(
              (msg) =>
                msg.id === convId ||
                msg.id === realConversationId ||
                msg.id === placeholderId ||
                msg.id.startsWith('placeholder_')
            );

            console.log('[CHAT] Found message at index:', idx);

            if (idx !== -1) {
              const newMessages = prev.map((msg, i) => {
                if (i === idx) {
                  const newContent = msg.content + text;
                  console.log('[CHAT] Updating message content:', msg.content.length, '->', newContent.length);
                  return { ...msg, id: convId || msg.id, content: newContent };
                }
                return msg;
              });
              return newMessages;
            }
            console.log('[CHAT] No message found to update!');
            return prev;
          });
        },
        onComplete: (finalSessionId: string, finalConversationId: string) => {
          console.log('[CHAT] Start message stream completed', { finalSessionId, finalConversationId });
          if (finalSessionId && !sessionId) {
            setSessionId(finalSessionId);
          }
          if (user) {
            loadUserSessions(user.userId);
          }
          setChatLoading(false);
          isProcessingRef.current = false;
          chatSSEService.disconnect(`chat_${streamSessionId}`);
        },
        onError: (errorMsg: string) => {
          console.error('[CHAT] SSE error for start message:', errorMsg);
          isProcessingRef.current = false;
          setChatLoading(false);
          const fallback = errorMsg || 'Sorry, I encountered an error. Please try again.';
          setMessages((prev) => {
            if (prev.length === 0) {
              return [{ id: Date.now().toString(), type: 'bot' as const, content: fallback, timestamp: new Date() }];
            }
            // Replace any empty placeholder (shows "Thinking...") with the error message
            const hasEmptyPlaceholder = prev.some((m) => m.type === 'bot' && !m.content);
            if (hasEmptyPlaceholder) {
              return prev.map((m) =>
                m.type === 'bot' && !m.content ? { ...m, content: fallback } : m
              );
            }
            return prev;
          });
          chatSSEService.disconnect(`chat_${streamSessionId}`);
        },
      });
    } catch (error: any) {
      console.error('Error starting chat:', error);
      isProcessingRef.current = false;
      setChatLoading(false);

      const errorMessage = error?.response?.data?.message || error?.message;
      if (errorMessage && (errorMessage.includes('API key') || errorMessage.includes('companion setup') || errorMessage.includes('temporarily unavailable'))) {
        const errorMsg: ChatMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: errorMessage,
          timestamp: new Date(),
        };
        setMessages([errorMsg]);
        toast.error(errorMessage);
      } else {
        const welcomeMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: `Welcome to ${event!.name}! I'm your AI assistant, ready to help you with any questions about the course content. What would you like to know?`,
          timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
      }
    }
  }, [user, event, chatVisible, useRAG, sessionId, enableAutoScroll]);

  // Reset chat initialized state when chat is closed
  useEffect(() => {
    if (!chatVisible) {
      chatInitializedRef.current = false;
      activeStreamRef.current = null;
      setSessionId(null);
    }
  }, [chatVisible]);

  useEffect(() => {
    if (chatVisible && user && !chatInitializedRef.current) {
      startChat();
    }
  }, [chatVisible, user, startChat]);

  // ── Detect API key errors from any message path ───────────────────────────
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.type === 'bot' && last.content && (
      last.content.includes('Knowledge Assistant is currently unavailable') ||
      last.content.includes('API key') ||
      last.content.includes('companion setup') ||
      last.content.includes('temporarily unavailable')
    )) {
      setApiKeysNotConfigured(true);
    }
  }, [messages]);

  // Unified Chat Handler (supports both RAG and regular modes)
  const sendMessage = async () => {
    if (!inputMessage.trim() || !event) return;

    if (!user) {
      toast.warning('Please login to start chatting');
      return;
    }

    if (isProcessingRef.current) {
      console.log('sendMessage: Already processing, skipping duplicate call');
      return;
    }

    enableAutoScroll();
    isProcessingRef.current = true;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setChatLoading(true);

    try {
      const chatRequest = {
        userId: user!.userId,
        eventId: event._id,
        message: currentMessage,
        sessionId: sessionId || undefined,
        useRAG: useRAG,
      };

      console.log(`Sending ${useRAG ? 'RAG-enabled' : 'regular'} streaming chat request:`, chatRequest);

      const streamSessionId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      activeStreamRef.current = streamSessionId;

      const placeholderId = `placeholder_${streamSessionId}`;
      let realConversationId: string | null = null;

      setMessages((prev) => [
        ...prev,
        {
          id: placeholderId,
          type: 'bot' as const,
          content: '',
          timestamp: new Date(),
          metadata: { isRAG: useRAG, chunksUsed: 0 },
        },
      ]);

      chatSSEService.connect(streamSessionId, {
        onConnect: () => {
          console.log('[CHAT] SSE connected, streamId:', streamSessionId);
          if (activeStreamRef.current !== streamSessionId) return;

          apiService
            .post('/chat/stream', { ...chatRequest, streamSessionId })
            .catch((error) => {
              console.error('[CHAT] Failed to start processing:', error);
              isProcessingRef.current = false;
              setChatLoading(false);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === placeholderId
                    ? {
                        ...msg,
                        content:
                          error?.response?.data?.message ||
                          'Failed to process. Please try again.',
                      }
                    : msg
                )
              );
              chatSSEService.disconnect(`chat_${streamSessionId}`);
            });
        },
        onStart: (convId: string) => {
          if (activeStreamRef.current !== streamSessionId) return;
          console.log('[CHAT] Response started:', convId);
          realConversationId = convId;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderId ? { ...msg, id: convId } : msg
            )
          );
        },
        onToken: (text: string, convId: string) => {
          if (activeStreamRef.current !== streamSessionId) return;
          console.log('[CHAT] Token:', text.substring(0, 20) + '...', 'convId:', convId);

          setMessages((prev) => {
            const idx = prev.findIndex(
              (msg) =>
                msg.id === convId ||
                msg.id === realConversationId ||
                msg.id === placeholderId ||
                msg.id.startsWith('placeholder_')
            );

            if (idx !== -1) {
              return prev.map((msg, i) =>
                i === idx
                  ? { ...msg, id: convId || msg.id, content: msg.content + text }
                  : msg
              );
            }
            return prev;
          });
        },
        onRAGInfo: (chunksRetrieved: number) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderId || msg.id === realConversationId
                ? { ...msg, metadata: { ...msg.metadata, chunksUsed: chunksRetrieved } }
                : msg
            )
          );
        },
        onComplete: (finalSessionId: string, finalConversationId: string) => {
          console.log('[CHAT] Stream completed', { finalSessionId, finalConversationId });
          if (finalSessionId && !sessionId) {
            setSessionId(finalSessionId);
          }
          if (user) {
            loadUserSessions(user.userId);
          }
          setChatLoading(false);
          isProcessingRef.current = false;
          chatSSEService.disconnect(`chat_${streamSessionId}`);
        },
        onError: (errorMsg: string) => {
          console.error('[CHAT] SSE error:', errorMsg);
          const errorMessage: ChatMessage = {
            id: Date.now().toString(),
            type: 'bot',
            content: errorMsg || 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
          setChatLoading(false);
          isProcessingRef.current = false;
          chatSSEService.disconnect(`chat_${streamSessionId}`);
        },
      });
    } catch (error: any) {
      console.error('Chat error:', error);
      console.error('Error response:', error?.response?.data);

      let errorMsg = 'Sorry, I encountered an error. Please try again.';
      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
        if (errorMsg.includes('API key') || errorMsg.includes('companion setup') || errorMsg.includes('temporarily unavailable')) {
          toast.error(errorMsg);
        }
      } else if (error?.response?.status === 400) {
        errorMsg = 'Invalid request. Please check your input and try again.';
      }

      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'bot',
        content: errorMsg,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setChatLoading(false);
      isProcessingRef.current = false;
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const inputCls =
    'w-full rounded-lg border border-gray-300 px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const errorCls = 'text-xs text-red-500 mt-1';
  const RequiredMark = () => <span className="text-red-500 ml-0.5">*</span>;

  const FieldError: React.FC<{ msg?: string }> = ({ msg }) =>
    msg ? <p className={errorCls}>{msg}</p> : null;

  // ---------------------------------------------------------------------------
  // Loading / not found screens
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <h2 className="text-2xl font-semibold text-gray-700">Knowledge Assistant not found</h2>
      </div>
    );
  }

  if (showWaThankYou) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
        <div className="w-full max-w-sm text-center bg-white rounded-2xl shadow-lg p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-3">You're all set!</h1>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            Your account has been created. Return to WhatsApp and send a message to start chatting.
          </p>
          {/* <a
            href="https://wa.me/"
            className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 active:bg-green-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors text-base"
          >
            Open WhatsApp
          </a> */}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Hero section
  // ---------------------------------------------------------------------------

  const heroBg = (event as any).signinPageHeroBgColor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

  const heroContent = (
    <motion.div
      className="text-white py-5 text-center relative overflow-hidden"
      style={{ background: heroBg }}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* SVG background decoration */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1000 1000\"><polygon fill=\"%23ffffff08\" points=\"0,1000 1000,0 1000,1000\"/></svg>')",
          backgroundSize: 'cover',
        }}
      />
      <div
        className="relative z-10 px-4"
        style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', minHeight: isMobile ? 260 : 420 }}
      >
        {/* Profile Dropdown - Top Right */}
        {user && (
          <div
            className="absolute z-10"
            style={{
              top: isMobile ? 10 : 20,
              right: isMobile ? 10 : 20,
            }}
          >
            <div className="relative">
              <button
                type="button"
                onClick={() => setProfileDropdownOpen((o) => !o)}
                className="flex items-center gap-2 text-white border border-white/30 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 hover:bg-white/20 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                  <UserIcon size={14} />
                </div>
                {!isMobile && <span className="text-sm">{user.name}</span>}
              </button>
              {profileDropdownOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-100 z-20 py-1">
                  <button
                    type="button"
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      handleUpdateProfile();
                    }}
                  >
                    <Pencil size={14} />
                    Update Profile
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    type="button"
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      handleSignOut();
                    }}
                  >
                    <LogOut size={14} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div
          className="flex flex-col items-center gap-4"
          style={{ minHeight: isMobile ? 260 : 420 }}
        >
          {event.imageUrl && (
            <motion.div
              className="w-full flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <div
                style={{
                  width: isMobile ? '100%' : 640,
                  maxWidth: '100%',
                  borderRadius: 12,
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.25)',
                }}
              >
                <img
                  src={event.imageUrl}
                  alt={event.name}
                  style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'contain', backgroundColor: 'transparent' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            </motion.div>
          )}

          <div className="w-full max-w-3xl text-center flex flex-col items-center">
            <motion.h1
              className={`font-bold ${isMobile ? 'text-2xl' : 'text-4xl'} mb-3`}
              style={{ color: (event as any).signinPageTitleColor || '#ffffff' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
            >
              {event.name}
            </motion.h1>

            <motion.p
              className="text-base max-w-2xl"
              style={{ color: (event as any).signinPageDescriptionColor || 'rgba(255,255,255,0.9)' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              {event.description}
            </motion.p>

            {user && (
              <motion.div
                className="mt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.65, duration: 0.6 }}
              >
                <button
                  type="button"
                  onClick={startChat}
                  className="flex items-center gap-2 px-8 py-3 rounded-lg font-medium text-white text-base"
                  style={{ background: 'linear-gradient(135deg, #52c41a 0%, #1890ff 100%)', border: 'none' }}
                >
                  <UserIcon size={18} />
                  Welcome, {user.name}! Start Chatting
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen">
      {/* Click-away for profile dropdown */}
      {profileDropdownOpen && (
        <div className="fixed inset-0 z-10" onClick={() => setProfileDropdownOpen(false)} />
      )}

      {!user ? (
        <div
          className={`flex min-h-screen ${isMobile ? 'flex-col' : 'flex-row'}`}
          style={(event as any).signinPageBgColor ? { backgroundColor: (event as any).signinPageBgColor } : { backgroundColor: '#ffffff' }}
        >
          {/* Left panel: hero */}
          <div className={`flex-1 flex items-center justify-center p-8 ${isMobile ? '' : 'min-h-screen'}`}>
            {heroContent}
          </div>

          {/* Right panel: sign up / login form */}
          <div className={`flex-1 flex items-center justify-center ${isMobile ? 'p-4' : 'p-8'}`}>
            <motion.div
              id="signup"
              className="w-full max-w-xl"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants} className="text-center mb-6">
                <p
                  className="text-lg mb-4"
                  style={{ color: (event as any).signinPageInstructionColor || '#6b7280' }}
                >
                  {isLogin
                    ? ((event as any).signinPageSigninText || 'Sign in to continue interacting with our Knowledge Companion')
                    : ((event as any).signinPageSignupText || 'Create your account to start interacting with our Knowledge Companion')}
                </p>
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsLogin(false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      !isLogin
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Sign Up
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsLogin(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      isLogin
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Sign In
                  </button>
                </div>
              </motion.div>

              <motion.div variants={itemVariants}>
                <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6">
                  {isLogin ? (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <label className={labelCls}>{loginFieldLabel}<RequiredMark /></label>
                        <input
                          type={loginFieldType}
                          className={inputCls}
                          placeholder={loginFieldPlaceholder}
                          value={loginValues.identifier || ''}
                          onChange={(e) =>
                            setLoginValues((v) => ({ ...v, identifier: e.target.value }))
                          }
                        />
                        <FieldError msg={loginErrors.identifier} />
                      </div>
                      <div>
                        <label className={labelCls}>Password<RequiredMark /></label>
                        <input
                          type="password"
                          className={inputCls}
                          placeholder="Enter your password"
                          value={loginValues.password || ''}
                          onChange={(e) =>
                            setLoginValues((v) => ({ ...v, password: e.target.value }))
                          }
                        />
                        <FieldError msg={loginErrors.password} />
                      </div>
                      {event?.requiresEmailVerification && (
                        <div className="text-right">
                          <button
                            type="button"
                            onClick={() => setForgotPasswordVisible(true)}
                            className="text-blue-600 text-sm hover:underline"
                          >
                            Forgot Password?
                          </button>
                        </div>
                      )}
                      <button
                        type="submit"
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg text-base transition-colors"
                      >
                        <UserIcon size={16} />
                        Sign In &amp; Continue Chatting
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleSignup} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelCls}>Full Name<RequiredMark /></label>
                          <input
                            type="text"
                            className={inputCls}
                            placeholder="Enter your full name"
                            value={signupValues.name || ''}
                            onChange={(e) =>
                              setSignupValues((v) => ({ ...v, name: e.target.value }))
                            }
                          />
                          <FieldError msg={signupErrors.name} />
                        </div>
                        <div>
                          <label className={labelCls}>Email Address{(event as any)?.requireEmail && <RequiredMark />}</label>
                          <input
                            type="email"
                            className={inputCls}
                            placeholder="Enter your email"
                            value={signupValues.email || ''}
                            onChange={(e) =>
                              setSignupValues((v) => ({ ...v, email: e.target.value }))
                            }
                          />
                          <FieldError msg={signupErrors.email} />
                        </div>
                      </div>

                      <div>
                        <label className={labelCls}>Phone Number{((event as any)?.otpChannel === 'whatsapp' || (event as any)?.requirePhone) && <RequiredMark />}</label>
                        <div className="flex gap-2">
                          <select
                            className="rounded-lg border border-gray-300 px-2 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            value={phoneCountryCode}
                            onChange={(e) => setPhoneCountryCode(e.target.value)}
                          >
                            {COUNTRY_CODES.map((c) => (
                              <option key={c.code} value={c.code}>{c.code} {c.name}</option>
                            ))}
                          </select>
                          <input
                            type="tel"
                            className={inputCls}
                            placeholder="10-digit number"
                            value={signupValues.phone || ''}
                            onChange={(e) =>
                              setSignupValues((v) => ({ ...v, phone: e.target.value.replace(/\D/g, '') }))
                            }
                          />
                        </div>
                        <FieldError msg={signupErrors.phone} />
                      </div>

                      <div>
                        <label className={labelCls}>Password<RequiredMark /></label>
                        <input
                          type="password"
                          className={inputCls}
                          placeholder="Create a password (min 6 characters)"
                          value={signupValues.password || ''}
                          onChange={(e) =>
                            setSignupValues((v) => ({ ...v, password: e.target.value }))
                          }
                        />
                        <FieldError msg={signupErrors.password} />
                      </div>

                      {signupFormFields.length > 0 ? (
                        signupFormFields.map((f) => (
                          <div key={f.fieldCode}>
                            <label className={labelCls}>{f.fieldName}{f.isRequired && <RequiredMark />}</label>
                            {f.fieldType === 'text' && (
                              <input
                                type="text"
                                className={inputCls}
                                placeholder={f.fieldName}
                                value={signupValues[f.fieldCode] || ''}
                                onChange={(e) =>
                                  setSignupValues((v) => ({ ...v, [f.fieldCode]: e.target.value }))
                                }
                              />
                            )}
                            {f.fieldType === 'boolean' && (
                              <button
                                type="button"
                                onClick={() =>
                                  setSignupValues((v) => ({
                                    ...v,
                                    [f.fieldCode]: !v[f.fieldCode],
                                  }))
                                }
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  signupValues[f.fieldCode] ? 'bg-blue-600' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    signupValues[f.fieldCode] ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            )}
                            {f.fieldType === 'dropdown' && (
                              <select
                                className={inputCls}
                                value={signupValues[f.fieldCode] || ''}
                                onChange={(e) =>
                                  setSignupValues((v) => ({ ...v, [f.fieldCode]: e.target.value }))
                                }
                              >
                                <option value="">Select {f.fieldName}</option>
                                {(f.options || []).map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            )}
                            <FieldError msg={signupErrors[f.fieldCode]} />
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className={labelCls}>Occupation</label>
                              <input
                                type="text"
                                className={inputCls}
                                placeholder="Your job title or profession"
                                value={signupValues.occupation || ''}
                                onChange={(e) =>
                                  setSignupValues((v) => ({ ...v, occupation: e.target.value }))
                                }
                              />
                            </div>
                            <div>
                              <label className={labelCls}>Industry</label>
                              <input
                                type="text"
                                className={inputCls}
                                placeholder="Your industry or business sector"
                                value={signupValues.businessNature || ''}
                                onChange={(e) =>
                                  setSignupValues((v) => ({ ...v, businessNature: e.target.value }))
                                }
                              />
                            </div>
                          </div>
                          <div>
                            <label className={labelCls}>Additional Details</label>
                            <textarea
                              className={inputCls}
                              rows={3}
                              placeholder="Tell us more about your background to get personalized responses..."
                              value={signupValues.businessDetails || ''}
                              onChange={(e) =>
                                setSignupValues((v) => ({
                                  ...v,
                                  businessDetails: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </>
                      )}

                      <button
                        type="submit"
                        disabled={signupLoading}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium py-3 rounded-lg text-base transition-colors"
                      >
                        {signupLoading ? (
                          <>
                            <Spinner size="sm" />
                            Signing up...
                          </>
                        ) : (
                          <>
                            <UserIcon size={16} />
                            Create Account &amp; Start Chatting
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </motion.div>
            </motion.div>
          </div>

        </div>
      ) : (
        <>{heroContent}</>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Chat Modal                                                          */}
      {/* ------------------------------------------------------------------ */}
      {chatVisible && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-hidden md:overflow-auto">
          <div
            className={`bg-white shadow-2xl flex flex-col ${
              isMobile ? 'w-full h-screen rounded-none' : 'rounded-2xl mt-5'
            }`}
            style={{ width: isMobile ? '100%' : 1200, maxWidth: '100vw' }}
          >
            {/* Modal header */}
            <div className="relative flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Bot size={18} className="text-blue-600" />
                <span className="font-semibold text-gray-800 truncate max-w-[140px] sm:max-w-none">{event.name}</span>
                <button
                  type="button"
                  onClick={clearChat}
                  className="text-sm px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Clear chat
                </button>
                {/* Desktop-only: Request a callback */}
                <button
                  type="button"
                  onClick={() => {
                    if (!user) {
                      toast.warning('Please login to request a callback');
                      return;
                    }
                    setCallbackModalVisible(true);
                  }}
                  className="hidden md:inline-flex text-sm px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Request a callback
                </button>
              </div>
              <div className="flex items-center gap-2">
                {/* Desktop-only: Go to new experience */}
                <button
                  type="button"
                  onClick={() => navigate(`/event/${eventId}/chat`)}
                  className="hidden md:inline-flex text-sm px-3 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
                >
                  Go to new experience →
                </button>
                {/* Mobile-only: hamburger */}
                <button
                  type="button"
                  onClick={() => setMenuOpen(v => !v)}
                  className="md:hidden p-1 rounded hover:bg-gray-100 text-gray-500"
                  aria-label="Menu"
                >
                  {menuOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
                <button
                  type="button"
                  onClick={() => setChatVisible(false)}
                  className="p-1 rounded hover:bg-gray-100 text-gray-500"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Mobile dropdown menu */}
              {menuOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-40 md:hidden"
                    onClick={() => setMenuOpen(false)}
                  />
                  {/* Dropdown */}
                  <div className="absolute top-full right-0 mt-1 w-56 bg-white rounded-xl shadow-lg border border-gray-100 z-50 md:hidden py-1">
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        if (!user) {
                          toast.warning('Please login to request a callback');
                          return;
                        }
                        setCallbackModalVisible(true);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Request a callback
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate(`/event/${eventId}/chat`);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-blue-600 font-medium hover:bg-blue-50 transition-colors"
                    >
                      Go to new experience →
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Chat body */}
            <div
              className={`flex flex-col bg-white ${isMobile ? 'flex-1 overflow-hidden' : ''}`}
              style={isMobile ? undefined : { height: '80vh' }}
            >
              {/* Messages */}
              <div
                ref={messagesContainerRef}
                onScroll={handleMessagesScroll}
                className="flex-1 overflow-y-auto p-6 bg-gray-50"
              >
                <AnimatePresence>
                  {messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      className={`mb-4 flex ${msg.type === 'bot' ? 'justify-start' : 'justify-end'}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div
                        className={`max-w-[70%] px-4 py-3 rounded-2xl shadow-sm break-words ${
                          msg.type === 'bot'
                            ? 'bg-gray-100 text-gray-900'
                            : 'bg-blue-500 text-white'
                        } md:max-w-[85%]`}
                      >
                        {msg.type === 'bot' ? (
                          msg.content ? (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                h1: ({ children }) => (
                                  <h1 style={{ fontSize: '18px', marginBottom: '8px', color: '#1890ff' }}>
                                    {children}
                                  </h1>
                                ),
                                h2: ({ children }) => (
                                  <h2 style={{ fontSize: '16px', marginBottom: '6px', color: '#1890ff' }}>
                                    {children}
                                  </h2>
                                ),
                                h3: ({ children }) => (
                                  <h3 style={{ fontSize: '14px', marginBottom: '4px', color: '#1890ff' }}>
                                    {children}
                                  </h3>
                                ),
                                p: ({ children }) => (
                                  <p style={{ marginBottom: '8px', lineHeight: '1.5' }}>{children}</p>
                                ),
                                ul: ({ children }) => (
                                  <ul style={{ marginBottom: '8px', paddingLeft: '20px' }}>{children}</ul>
                                ),
                                ol: ({ children }) => (
                                  <ol style={{ marginBottom: '8px', paddingLeft: '20px' }}>{children}</ol>
                                ),
                                li: ({ children }) => (
                                  <li style={{ marginBottom: '4px' }}>{children}</li>
                                ),
                                strong: ({ children }) => (
                                  <strong style={{ fontWeight: 'bold', color: '#1890ff' }}>{children}</strong>
                                ),
                                em: ({ children }) => (
                                  <em style={{ fontStyle: 'italic', color: '#722ed1' }}>{children}</em>
                                ),
                                code: ({ children }) => (
                                  <code
                                    style={{
                                      backgroundColor: '#f5f5f5',
                                      padding: '2px 4px',
                                      borderRadius: '3px',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {children}
                                  </code>
                                ),
                                blockquote: ({ children }) => (
                                  <blockquote
                                    style={{
                                      borderLeft: '4px solid #1890ff',
                                      paddingLeft: '12px',
                                      margin: '8px 0',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {children}
                                  </blockquote>
                                ),
                                table: ({ children }) => (
                                  <table
                                    style={{
                                      width: '100%',
                                      borderCollapse: 'collapse',
                                      marginBottom: '8px',
                                    }}
                                  >
                                    {children}
                                  </table>
                                ),
                                th: ({ children }) => (
                                  <th
                                    style={{
                                      border: '1px solid #d9d9d9',
                                      padding: '8px',
                                      backgroundColor: '#fafafa',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {children}
                                  </th>
                                ),
                                td: ({ children }) => (
                                  <td style={{ border: '1px solid #d9d9d9', padding: '8px' }}>
                                    {children}
                                  </td>
                                ),
                              }}
                            >
                              {msg.content}
                            </ReactMarkdown>
                          ) : (
                            <div className="text-gray-500 flex items-center gap-2">
                              <Spinner size="sm" />
                              <span className="italic">Thinking...</span>
                            </div>
                          )
                        ) : (
                          msg.content
                        )}

                        {msg.type === 'bot' && msg.content && (
                          <div className="mt-2 flex gap-1 items-center opacity-80">
                            <button
                              type="button"
                              title="Helpful"
                              onClick={() => handleQuickFeedback(msg.id, 5, 'Thumbs Up')}
                              disabled={submittingFeedbackFor === msg.id}
                              className="p-1 rounded hover:bg-gray-200 text-gray-500 disabled:opacity-50"
                              aria-label="Thumbs up"
                            >
                              <ThumbsUp size={14} />
                            </button>
                            <button
                              type="button"
                              title="Not helpful"
                              onClick={() => handleQuickFeedback(msg.id, 1, 'Thumbs Down')}
                              disabled={submittingFeedbackFor === msg.id}
                              className="p-1 rounded hover:bg-gray-200 text-gray-500 disabled:opacity-50"
                              aria-label="Thumbs down"
                            >
                              <ThumbsDown size={14} />
                            </button>
                            <button
                              type="button"
                              title="Report issue"
                              onClick={() => setReportModal({ open: true, messageId: msg.id })}
                              className="p-1 rounded hover:bg-gray-200 text-gray-500"
                              aria-label="Report"
                            >
                              <Flag size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Fallback loading indicator */}
                {chatLoading && messages.length === 0 && (
                  <div className="mb-4 flex justify-start">
                    <div className="max-w-[70%] px-4 py-3 rounded-2xl shadow-sm bg-gray-100 text-gray-900">
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <span>Assistant is thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                {messages.length === 0 && !chatLoading && (
                  <div className="text-center py-16 text-gray-500">
                    <Bot size={48} className="mx-auto mb-4 text-gray-300" />
                    <div className="text-lg mb-2">Welcome to {event.name}!</div>
                    <div className="text-sm">
                      Start a conversation by typing your question below.
                    </div>
                  </div>
                )}
              </div>

              {/* Input area */}
              <div className="px-6 py-4 border-t border-gray-100 bg-white">
                <div className="flex gap-2">
                  <textarea
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Ask a question... (Shift+Enter for new line)"
                    disabled={chatLoading || !user || apiKeysNotConfigured}
                    rows={3}
                  />
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || chatLoading || !user || apiKeysNotConfigured}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg flex items-center gap-1 transition-colors self-start"
                  >
                    <Send size={16} />
                    Send
                  </button>
                </div>
                {!user && (
                  <p className="mt-2 text-xs text-red-500 text-center">
                    Please login to start chatting
                  </p>
                )}
              </div>

              {/* Disclaimer */}
              <div className="text-center px-6 py-2 text-xs text-gray-400 border-t border-gray-100 bg-gray-50">
                AI may generate inaccurate information. Please verify important details.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Notifications Modal                                                 */}
      {/* ------------------------------------------------------------------ */}
      {notifOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 font-semibold text-gray-800">
                <Bell size={16} className="text-blue-600" />
                Knowledge Assistant Notifications
              </div>
              <button
                type="button"
                onClick={() => setNotifOpen(false)}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={16} />
              </button>
            </div>
            <ul className="overflow-y-auto divide-y divide-gray-100 flex-1">
              {notifications.map((n) => (
                <li key={n._id} className="px-5 py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                        n.priority === 'critical'
                          ? 'bg-red-50 text-red-600 border-red-200'
                          : n.priority === 'warning'
                          ? 'bg-orange-50 text-orange-600 border-orange-200'
                          : 'bg-blue-50 text-blue-600 border-blue-200'
                      }`}
                    >
                      {n.priority.toUpperCase()}
                    </span>
                    <span className="font-medium text-gray-800 text-sm">{n.title}</span>
                  </div>
                  <div className="text-sm text-gray-600 whitespace-pre-wrap">{n.body}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {new Date(n.createdAt).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Report Issue Modal                                                  */}
      {/* ------------------------------------------------------------------ */}
      {reportModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="font-semibold text-gray-800">Report an issue</span>
              <button
                type="button"
                onClick={() => {
                  setReportModal(null);
                  setReportValues({});
                  setReportErrors({});
                }}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className={labelCls}>Rating (-1 to 5)</label>
                <input
                  type="number"
                  min={-1}
                  max={5}
                  className={inputCls}
                  placeholder="-1 to 5"
                  value={reportValues.rating ?? ''}
                  onChange={(e) =>
                    setReportValues((v) => ({ ...v, rating: e.target.value }))
                  }
                />
                <FieldError msg={reportErrors.rating} />
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <select
                  className={inputCls}
                  value={reportValues.category || ''}
                  onChange={(e) =>
                    setReportValues((v) => ({ ...v, category: e.target.value }))
                  }
                >
                  <option value="">Select category</option>
                  {['accuracy', 'hallucination', 'tone', 'latency', 'ui', 'other'].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <FieldError msg={reportErrors.category} />
              </div>
              <div>
                <label className={labelCls}>Summary</label>
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Short title"
                  value={reportValues.summary || ''}
                  onChange={(e) =>
                    setReportValues((v) => ({ ...v, summary: e.target.value }))
                  }
                />
                <FieldError msg={reportErrors.summary} />
              </div>
              <div>
                <label className={labelCls}>Details</label>
                <textarea
                  className={inputCls}
                  rows={4}
                  placeholder="Describe the issue..."
                  value={reportValues.details || ''}
                  onChange={(e) =>
                    setReportValues((v) => ({ ...v, details: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setReportModal(null);
                  setReportValues({});
                  setReportErrors({});
                }}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReport}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Callback Request Modal                                              */}
      {/* ------------------------------------------------------------------ */}
      {callbackModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="font-semibold text-gray-800">Request a callback</span>
              <button
                type="button"
                onClick={() => {
                  setCallbackModalVisible(false);
                  setCallbackValues({});
                  setCallbackErrors({});
                }}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className={labelCls}>Issue</label>
                <input
                  type="text"
                  className={inputCls}
                  placeholder="What do you need help with?"
                  value={callbackValues.issue || ''}
                  onChange={(e) =>
                    setCallbackValues((v) => ({ ...v, issue: e.target.value }))
                  }
                />
                <FieldError msg={callbackErrors.issue} />
              </div>
              <div>
                <label className={labelCls}>Expected resolution</label>
                <textarea
                  className={inputCls}
                  rows={3}
                  placeholder="What outcome are you expecting?"
                  value={callbackValues.expectedResolution || ''}
                  onChange={(e) =>
                    setCallbackValues((v) => ({ ...v, expectedResolution: e.target.value }))
                  }
                />
                <FieldError msg={callbackErrors.expectedResolution} />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setCallbackModalVisible(false);
                  setCallbackValues({});
                  setCallbackErrors({});
                }}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCallbackRequest}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Profile Update Modal                                                */}
      {/* ------------------------------------------------------------------ */}
      {profileModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 font-semibold text-gray-800">
                <Pencil size={16} />
                Update Profile
              </div>
              <button
                type="button"
                onClick={() => {
                  setProfileModalVisible(false);
                  setProfileValues({});
                  setProfileErrors({});
                }}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Full Name</label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="Enter your full name"
                    value={profileValues.name || ''}
                    onChange={(e) =>
                      setProfileValues((v) => ({ ...v, name: e.target.value }))
                    }
                  />
                  <FieldError msg={profileErrors.name} />
                </div>
                <div>
                  <label className={labelCls}>Email Address</label>
                  <input
                    type="email"
                    className={inputCls}
                    placeholder="Enter your email"
                    value={profileValues.email || ''}
                    onChange={(e) =>
                      setProfileValues((v) => ({ ...v, email: e.target.value }))
                    }
                  />
                  <FieldError msg={profileErrors.email} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Occupation</label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="Your job title or profession"
                    value={profileValues.occupation || ''}
                    onChange={(e) =>
                      setProfileValues((v) => ({ ...v, occupation: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className={labelCls}>Industry</label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="Your industry or business sector"
                    value={profileValues.businessNature || ''}
                    onChange={(e) =>
                      setProfileValues((v) => ({ ...v, businessNature: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Additional Details</label>
                <textarea
                  className={inputCls}
                  rows={3}
                  placeholder="Tell us more about your background to get personalized responses..."
                  value={profileValues.businessDetails || ''}
                  onChange={(e) =>
                    setProfileValues((v) => ({ ...v, businessDetails: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setProfileModalVisible(false);
                  setProfileValues({});
                  setProfileErrors({});
                }}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProfileSave}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Forgot Password Modal                                               */}
      {/* ------------------------------------------------------------------ */}
      {forgotPasswordVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 font-semibold text-gray-800">
                <Lock size={16} />
                Forgot Password
              </div>
              <button
                type="button"
                onClick={() => {
                  setForgotPasswordVisible(false);
                  setForgotPasswordValues({});
                  setForgotPasswordErrors({});
                }}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleForgotPassword} className="px-5 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                Enter your email address and we'll generate a new password and send it to your
                email. You can then use it to login.
              </p>
              <div>
                <label className={labelCls}>Email Address</label>
                <input
                  type="email"
                  className={inputCls}
                  placeholder="Enter your email address"
                  value={forgotPasswordValues.email || ''}
                  onChange={(e) =>
                    setForgotPasswordValues((v) => ({ ...v, email: e.target.value }))
                  }
                />
                <FieldError msg={forgotPasswordErrors.email} />
              </div>
              <button
                type="submit"
                disabled={passwordResetLoading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium py-3 rounded-lg text-sm transition-colors"
              >
                {passwordResetLoading ? <Spinner size="sm" /> : null}
                Send New Password
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordVisible(false);
                    setForgotPasswordValues({});
                    setForgotPasswordErrors({});
                  }}
                  className="text-blue-600 text-sm hover:underline"
                >
                  Back to Login
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* OTP Verification Modal                                              */}
      {/* ------------------------------------------------------------------ */}
      {otpModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 font-semibold text-gray-800">
              <Lock size={16} />
              {otpChannel === 'whatsapp' ? 'Verify via WhatsApp' : 'Verify your email'}
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                {otpChannel === 'whatsapp'
                  ? 'Enter the 6-digit code we sent to your WhatsApp number to continue.'
                  : 'Enter the 6-digit code we sent to your email to continue.'}
              </p>
              <div>
                <label className={labelCls}>Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className={inputCls}
                  placeholder="123456"
                  maxLength={6}
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value)}
                />
                <FieldError msg={otpError} />
              </div>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={otpResendLoading || otpLoading}
                className="text-blue-600 text-sm hover:underline disabled:opacity-50"
              >
                {otpResendLoading ? 'Resending...' : 'Resend code'}
              </button>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={otpLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {otpLoading ? <Spinner size="sm" /> : null}
                Verify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventLandingPage;
